import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { EvaluationTypeLabelsEnum, ProjectTypeLabelsEnum } from '@/app/utils/constants';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import { FolderIcon, AuctionIcon } from '@hugeicons/core-free-icons';
import { DocumentVersionViewerPage } from '@/app/components/shared/EntityDocuments/DocumentVersionPage';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/files/$documentId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId, tenderPackageId, bidId, documentId } = Route.useParams();

  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));

  return (
    <DocumentVersionViewerPage
      documentId={documentId}
      renderBreadcrumbs={(documentName) => (
        <Breadcrumbs
          projectId={projectId}
          tenderPackageId={tenderPackageId}
          bidId={bidId}
          project={project}
          evaluation={evaluation}
          bid={bid}
          documentName={documentName}
        />
      )}
    />
  );
}

function Breadcrumbs({
  projectId,
  tenderPackageId,
  bidId,
  project,
  evaluation,
  bid,
  documentName,
}: {
  projectId: string;
  tenderPackageId: string;
  bidId: string;
  project: { type: ProjectTypeLabelsEnum; name: string };
  evaluation: { name: string };
  bid: { bidAdditionalName?: string | null; companyName?: string };
  documentName?: string;
}) {
  const t = useTranslations('pages.bids');

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbMenu>
          <BreadcrumbMenuItem
            to="/project/$projectId/tender-packages/$tenderPackageId/bids"
            params={{ projectId, tenderPackageId }}
          >
            <HugeiconsIcon icon={AuctionIcon} />
            {t('title')}
          </BreadcrumbMenuItem>
          <BreadcrumbMenuItem
            to="/project/$projectId/tender-packages/$tenderPackageId"
            params={{ projectId, tenderPackageId }}
          >
            <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
            {evaluation.name}
          </BreadcrumbMenuItem>
          <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
            <ProjectTypeIcon type={project.type} />
            {project.name}
          </BreadcrumbMenuItem>
        </BreadcrumbMenu>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem className="max-md:hidden">
          <BreadcrumbLink
            to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
            params={{ projectId, tenderPackageId, bidId }}
          >
            <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem className="max-md:hidden">
          <BreadcrumbLink
            to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/files"
            params={{ projectId, tenderPackageId, bidId }}
          >
            <HugeiconsIcon icon={FolderIcon} />
            {t('nav.files')}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="max-md:hidden" />
        <BreadcrumbItem>
          <BreadcrumbPage>{documentName ?? '...'}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
